import {
  Component,
  inject,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { ButtonDirective, SpinDirective } from '@shared/directives/button.directive';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { AccountManagementService } from '@core/services/account-management.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  GENDER,
} from 'app/app.constants';

@Component({
  selector: 'app-no-nin-minor-account',
  standalone: true,
  imports: [
    CommonModule,
    SlideInRightModalComponent,
    FormsModule,
    ReactiveFormsModule,
    InputLabelComponent,
    InputComponent,
    ButtonDirective,
    BaseSelectDirective,
    SpinDirective,
  ],
  templateUrl: './no-nin-minor-account.component.html',
  styleUrls: ['./no-nin-minor-account.component.scss'],
})
export class NoNinMinorAccountComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  returnPathUrl: string = '';
  isLoading: boolean = false;
  isCheckingPhone1: boolean = false;
  isCheckingPhone2: boolean = false;
  isCheckingEmail: boolean = false;
  accountId!: string;
  accountHolderEmail: string = '';

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private toast = inject(NotificationFacade);
  private change = inject(ChangeDetectorRef);
  private accountService = inject(AccountManagementService);

  genderList = GENDER;

  minorForm = this.fb.nonNullable.group({
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    dob: ['', Validators.required],
    gender: [''],
    phone_number_1: [''],
    phone_number_2: [''],
    email: ['']
  });

  ngOnInit(): void {
    this.accountId = this.activatedRoute.snapshot.parent?.params['accountId'];
    
    this.setupPhoneValidation('phone_number_1');
    this.setupPhoneValidation('phone_number_2');
    this.setupEmailValidation();
    this.fetchAccountHolderEmail();
  }

  private setupPhoneValidation(controlName: 'phone_number_1' | 'phone_number_2'): void {
    const phoneControl = this.minorForm.get(controlName);

    if (phoneControl) {
      const phoneSub = phoneControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
        )
        .subscribe((phone) => {
          // Clear previous errors
          const currentErrors = phoneControl.errors;
          if (currentErrors) {
            delete currentErrors['phoneExists'];
            delete currentErrors['serverError'];
            phoneControl.setErrors(
              Object.keys(currentErrors).length > 0 ? currentErrors : null
            );
          }

          // Only check if phone has value (not required for phone_number_2)
          if (phone && phone.trim()) {
            this.checkPhoneDuplicate(phone, controlName);
          }
        });

      this.subs.push(phoneSub);
    }
  }

  private checkPhoneDuplicate(phone: string, controlName: 'phone_number_1' | 'phone_number_2'): void {
    if (controlName === 'phone_number_1') {
      this.isCheckingPhone1 = true;
    } else {
      this.isCheckingPhone2 = true;
    }
    this.change.detectChanges();

    const checkSub = this.accountService
      .checkUserDetail(undefined, phone)
      .subscribe({
        next: (response) => {
          if (controlName === 'phone_number_1') {
            this.isCheckingPhone1 = false;
          } else {
            this.isCheckingPhone2 = false;
          }

          const phoneControl = this.minorForm.get(controlName);

          if (response.exists && phoneControl) {
            phoneControl.setErrors({
              ...(phoneControl.errors || {}),
              phoneExists: true,
            });
          }

          this.change.detectChanges();
        },
        error: () => {
          if (controlName === 'phone_number_1') {
            this.isCheckingPhone1 = false;
          } else {
            this.isCheckingPhone2 = false;
          }

          const phoneControl = this.minorForm.get(controlName);
          
          if (phoneControl) {
            phoneControl.setErrors({
              ...(phoneControl.errors || {}),
              serverError: true,
            });
          }

          this.change.detectChanges();
        },
      });

    this.subs.push(checkSub);
  }

  private setupEmailValidation(): void {
    const emailControl = this.minorForm.get('email');

    if (emailControl) {
      const emailSub = emailControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
        )
        .subscribe((email) => {
          // Clear previous errors
          const currentErrors = emailControl.errors;
          if (currentErrors) {
            delete currentErrors['emailExists'];
            delete currentErrors['serverError'];
            emailControl.setErrors(
              Object.keys(currentErrors).length > 0 ? currentErrors : null
            );
          }

          if (email && email.trim()) {
            this.checkEmailDuplicate(email);
          }
        });

      this.subs.push(emailSub);
    }
  }

  private checkEmailDuplicate(email: string): void {
    this.isCheckingEmail = true;
    this.change.detectChanges();

    const checkSub = this.accountService
      .checkUserDetail(email, undefined)
      .subscribe({
        next: (response) => {
          this.isCheckingEmail = false;
          const emailControl = this.minorForm.get('email');

          if (response.exists && emailControl) {
            emailControl.setErrors({
              ...(emailControl.errors || {}),
              emailExists: true,
            });
          }

          this.change.detectChanges();
        },
        error: () => {
          this.isCheckingEmail = false;
          const emailControl = this.minorForm.get('email');

          if (emailControl) {
            emailControl.setErrors({
              ...(emailControl.errors || {}),
              serverError: true,
            });
          }

          this.change.detectChanges();
        },
      });

    this.subs.push(checkSub);
  }

  private fetchAccountHolderEmail(): void {
    const sub = this.accountService.getIndividualAccountInfo(this.accountId).subscribe({
      next: (response) => {
        this.accountHolderEmail = response.data.user.email;
      },
      error: () => {
        this.toast.error('Failed to fetch account holder email');
      },
    });

    this.subs.push(sub);
  }

  get minDate(): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split('T')[0];
  }

  get maxDate(): string{
    const date = new Date();
    date.setFullYear(date.getFullYear());
    return date.toISOString().split('T')[0];
  }

  submit(): void {
    if (this.minorForm.invalid) {
      this.minorForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    const { phone_number_2, ...formData } = this.minorForm.getRawValue();

    let finalEmail: string;
    let payloadEmail: string;

    if(formData.email?.trim()) {
      finalEmail = formData.email;
    } else {
      const [localPart, domain] = this.accountHolderEmail.split('@');
      finalEmail = `${localPart}+${formData.first_name.trim().toLowerCase()}@${domain}`;
    }
    payloadEmail = finalEmail.toLowerCase();
    const payload = {
      ...formData,
      email: payloadEmail,
      account_id: this.accountId,
      ...(phone_number_2 && { phone_number_2 }),
    };

    const createSub = this.accountService.createMinorAccount(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.router
          .navigateByUrl(`/admin/accounts/individual/accounts/${this.accountId}`)
          .then(() => {
            window.location.reload();
            this.toast.success('Minor account created successfully');
          });
      },
      error: () => {
        this.isLoading = false;
        this.toast.error('Failed to create minor account');
        this.change.detectChanges();
      },
    });

    this.subs.push(createSub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}